import React, { useRef, useEffect, useState, useCallback } from 'react'
import './MapModal.css'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import RouteControls from './RouteControls'
import MapErrorBoundary from './MapErrorBoundary'
import { MAP_CONSTANTS, US_STATES } from './mapConstants'
import { addBasemap } from './basemap'
import { removeMap } from './removeMap'
import { useDarkMode } from '../../context/DarkModeContext'
import {
	escapeHtml,
	validateRouteResponse,
	parseCoordinates,
	createStopsString,
} from './mapUtils'

const getStateAbbr = state => {
	if (!state) return ''
	if (state.length === 2) return state.toUpperCase()
	return US_STATES[state] || state
}

const MapModal = ({ mapUrl, onClose, load = {}, startingLocation = null }) => {
	const modalRef = useRef(null)
	const mapContainerRef = useRef(null)
	const leafletMapRef = useRef(null)
	const initialBoundsRef = useRef(null)
	const { isDarkMode } = useDarkMode()
	const [isLoading, setIsLoading] = useState(false)
	const [useEmbeddedMap, setUseEmbeddedMap] = useState(() => {
		try {
			const saved = localStorage.getItem('loadfetcher-use-embedded-map')
			if (saved === null) return true
			const parsed = JSON.parse(saved)
			return typeof parsed === 'boolean' ? parsed : true
		} catch {
			return true
		}
	})
	const [routeData, setRouteData] = useState(null)
	const [routeError, setRouteError] = useState(null)
	const [draggedIndex, setDraggedIndex] = useState(null)
	const [pendingDragStops, setPendingDragStops] = useState(null)
	const stopsStringRef = useRef('')
	const abortControllerRef = useRef(null)

	// Extract origin and destination from load data
	const origin = load?.pickup?.address
	const destination = load?.dropOff?.address

	// Initialize stops state
	const [stops, setStops] = useState([])
	const [isGeocodingStartingLocation, setIsGeocodingStartingLocation] =
		useState(false)

	const geocodeLocation = useCallback(async locationString => {
		try {
			const coords = parseCoordinates(locationString)
			if (coords) {
				return coords
			}

			// Use LoadFetcher's Mapbox endpoint
			const response = await fetch(
				`/api/geocode?query=${encodeURIComponent(locationString)}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: localStorage.getItem('jwtToken'),
					},
				}
			)

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.error || 'Geocoding failed')
			}

			const data = await response.json()

			if (data.features && data.features.length > 0) {
				const result = data.features[0]
				return {
					latitude: result.lat ?? result.center?.[1],
					longitude: result.lng ?? result.center?.[0],
					name: result.place_name || `${result.city}, ${result.state_id}` || locationString,
				}
			}
			return null
		} catch (error) {
			console.error('Error geocoding location:', error)
			throw error
		}
	}, [])

	useEffect(() => {
		const initializeStops = async () => {
			const newStops = []

			// Add starting location if provided
			if (startingLocation) {
				setIsGeocodingStartingLocation(true)
				const geocoded = await geocodeLocation(startingLocation)
				setIsGeocodingStartingLocation(false)

				if (geocoded) {
					newStops.push({
						id: 'starting',
						name: startingLocation,
						coordinates: [geocoded.latitude, geocoded.longitude],
					})
				}
			}

			// Check if load has multiple stops structure
			if (load?.stops && load.stops.length > 0) {
				// Use a Set to track which sequence numbers we've already added
				const addedSequenceNumbers = new Set()

				// Handle multi-stop loads
				load.stops.forEach(stopGroup => {
					if (stopGroup.stops && stopGroup.stops.length > 0) {
						stopGroup.stops.forEach(stop => {
							if (
								stop.location &&
								stop.location.latitude &&
								stop.location.longitude
							) {
								// Only add if we haven't seen this sequence number before
								if (!addedSequenceNumbers.has(stop.stopSequenceNumber)) {
									addedSequenceNumbers.add(stop.stopSequenceNumber)
									const cityName = stop.location.city
										? stop.location.city.charAt(0).toUpperCase() +
										  stop.location.city.slice(1).toLowerCase()
										: ''
									const stateAbbr = getStateAbbr(stop.location.state)
									newStops.push({
										id: `stop-${stop.stopSequenceNumber}`,
										name: stop.location.label
											? `${stop.location.label} ${cityName}, ${stateAbbr}`
											: `${cityName}, ${stateAbbr}`,
										coordinates: [
											stop.location.latitude,
											stop.location.longitude,
										],
										sequenceNumber: stop.stopSequenceNumber,
									})
								}
							}
						})
					}
				})
			} else if (load?.loads && load.loads.length > 0) {
				// Handle loads array structure
				load.loads.forEach((stop, index) => {
					if (stop.latitude && stop.longitude) {
						const cityName = stop.city
							? stop.city.charAt(0).toUpperCase() +
							  stop.city.slice(1).toLowerCase()
							: ''
						const stateAbbr = getStateAbbr(stop.state)
						newStops.push({
							id: `stop-${index}`,
							name: stop.label
								? `${stop.label} ${cityName}, ${stateAbbr}`
								: `${cityName}, ${stateAbbr}`,
							coordinates: [stop.latitude, stop.longitude],
						})
					}
				})
			} else if (origin && destination) {
				// Fallback to simple pickup/dropoff
				const originCity = origin.city
					? origin.city.charAt(0).toUpperCase() +
					  origin.city.slice(1).toLowerCase()
					: origin.city
				const destCity = destination.city
					? destination.city.charAt(0).toUpperCase() +
					  destination.city.slice(1).toLowerCase()
					: destination.city

				newStops.push({
					id: 'origin',
					name: `${originCity}, ${getStateAbbr(origin.state)}`,
					coordinates: [origin.latitude, origin.longitude],
				})

				newStops.push({
					id: 'destination',
					name: `${destCity}, ${getStateAbbr(destination.state)}`,
					coordinates: [destination.latitude, destination.longitude],
				})
			}

			setStops(newStops)
		}

		setRouteData(null)
		setRouteError(null)
		stopsStringRef.current = ''

		initializeStops()
	}, [load, origin, destination, startingLocation, geocodeLocation])

	useEffect(() => {
		const handleKeyDown = event => {
			if (event.key === 'Escape') {
				onClose()
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [onClose])

	useEffect(() => {
		try {
			const saved = localStorage.getItem('loadfetcher-use-embedded-map')
			if (saved === null) {
				setUseEmbeddedMap(true)
				return
			}
			const parsed = JSON.parse(saved)
			setUseEmbeddedMap(typeof parsed === 'boolean' ? parsed : true)
		} catch {
			setUseEmbeddedMap(true)
		}

		setRouteData(null)
		setRouteError(null)
		stopsStringRef.current = ''
	}, [mapUrl])

	const handleBackdropClick = event => {
		if (event.target === event.currentTarget) onClose()
	}

	const handleSwitchToggle = () => {
		const newValue = !useEmbeddedMap
		setUseEmbeddedMap(newValue)
		localStorage.setItem(
			'loadfetcher-use-embedded-map',
			JSON.stringify(newValue)
		)
	}

	const handleDragStart = useCallback(index => {
		setDraggedIndex(index)
		setPendingDragStops(null)
	}, [])

	const handleDragOver = useCallback(
		(e, index) => {
			e.preventDefault()
			if (draggedIndex === null || draggedIndex === index) return

			setPendingDragStops(prevPending => {
				const currentStops = prevPending || stops
				const newStops = [...currentStops]
				const draggedStop = newStops[draggedIndex]
				newStops.splice(draggedIndex, 1)
				newStops.splice(index, 0, draggedStop)
				return newStops
			})

			setDraggedIndex(index)
		},
		[draggedIndex, stops]
	)

	const handleDragEnd = useCallback(() => {
		if (pendingDragStops) {
			setStops(pendingDragStops)
			setPendingDragStops(null)
		}
		setDraggedIndex(null)
	}, [pendingDragStops])

	const handleDeleteStop = useCallback(
		index => {
			const newStops = stops.filter((_, i) => i !== index)
			setStops(newStops)
		},
		[stops]
	)

	const handleAddStop = useCallback(newStop => {
		setStops(prev => [...prev, newStop])
	}, [])

	const handleUpdateStop = useCallback((index, updatedData) => {
		setStops(prev => {
			const newStops = [...prev]
			newStops[index] = {
				...newStops[index],
				...updatedData,
			}
			return newStops
		})
	}, [])

	const handleSearchLocation = useCallback(async (query, abortSignal) => {
		try {
			const response = await fetch(
				`/api/geocode?query=${encodeURIComponent(query)}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: localStorage.getItem('jwtToken'),
					},
					signal: abortSignal,
				}
			)

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.error || 'Search failed')
			}

			const data = await response.json()

			const convertedData = {
				success: true,
				data:
					data.features?.map(feature => {
						const lat = feature.lat ?? feature.center?.[1]
						const lon = feature.lng ?? feature.center?.[0]
						const city = feature.city ?? feature.text
						const state = feature.state_id ?? ''
						const displayName = feature.place_name || `${city}, ${state}`

						return {
							lat,
							lon,
							display_name: displayName,
							address: {
								city,
								state,
								town: city,
							},
						}
					}) || [],
			}

			return convertedData
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error('Location search error:', error)
			}
			throw error
		}
	}, [])

	useEffect(() => {
		if (stops.length >= 2 && draggedIndex === null) {
			const currentStopsString = createStopsString(stops)

			if (currentStopsString !== stopsStringRef.current) {
				stopsStringRef.current = currentStopsString

				if (abortControllerRef.current) {
					try {
						abortControllerRef.current.abort('cleanup')
					} catch {}
				}

				const abortController = new AbortController()
				abortControllerRef.current = abortController

				const fetchRoute = async () => {
					setIsLoading(true)
					setRouteError(null)

					try {
						const locations = stops
							.filter(
								stop =>
									stop?.coordinates?.length >= 2 &&
									typeof stop.coordinates[0] === 'number' &&
									typeof stop.coordinates[1] === 'number' &&
									!isNaN(stop.coordinates[0]) &&
									!isNaN(stop.coordinates[1])
							)
							.map(stop => ({
								lat: stop.coordinates[0],
								lon: stop.coordinates[1],
							}))

						if (locations.length < 2) {
							setRouteError('At least 2 valid locations required')
							setIsLoading(false)
							return
						}

						const response = await fetch(`/api/route-planner/route`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization: localStorage.getItem('jwtToken'),
							},
							body: JSON.stringify({ locations }),
							signal: abortController.signal,
						})

						if (!response.ok) {
							const errorData = await response.json()
							throw new Error(errorData.message || 'Failed to fetch route')
						}

						if (!abortController.signal.aborted) {
							const data = await response.json()
							const validatedData = validateRouteResponse(data)
							setRouteData(validatedData.data)
						}
					} catch (error) {
						if (
							error.name !== 'AbortError' &&
							!abortController.signal.aborted
						) {
							console.error('❌ [ROUTE FETCH] Error:', error)
							setRouteError(error.message || 'Failed to fetch route')
						}
					} finally {
						if (!abortController.signal.aborted) {
							setIsLoading(false)
						}
					}
				}

				fetchRoute()
			}
		}

		return () => {
			if (abortControllerRef.current) {
				try {
					abortControllerRef.current.abort('cleanup')
				} catch {}
			}
		}
	}, [stops, draggedIndex])

	useEffect(() => {
		if (draggedIndex !== null) return

		removeMap(leafletMapRef.current)
		leafletMapRef.current = null

		if (mapContainerRef.current) {
			mapContainerRef.current.innerHTML = ''
			mapContainerRef.current.className = 'll-leaflet-map-container'
		}

		if (mapContainerRef.current) {
			try {
				let center, zoom

				if (routeData && routeData.polyline) {
					center = routeData.polyline[0]
					zoom = MAP_CONSTANTS.ZOOM_LEVELS.ROUTE
				} else if (stops.length > 0) {
					center = stops[0].coordinates
					zoom = MAP_CONSTANTS.ZOOM_LEVELS.SINGLE_STOP
				} else {
					center = MAP_CONSTANTS.DEFAULT_CENTER
					zoom = MAP_CONSTANTS.ZOOM_LEVELS.DEFAULT
				}

				const map = L.map(mapContainerRef.current, {
					center: center,
					zoom: zoom,
					zoomControl: false,
					attributionControl: true,
				})

				L.control
					.zoom({
						position: 'topright',
					})
					.addTo(map)

				const ResetControl = L.Control.extend({
					options: {
						position: 'topright',
					},
					onAdd: function (map) {
						const container = L.DomUtil.create(
							'div',
							'll-leaflet-reset-control leaflet-bar leaflet-control'
						)
						container.innerHTML =
							'<a href="#" title="Reset view" role="button" aria-label="Reset view"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="2" x2="12" y2="8"/><line x1="12" y1="16" x2="12" y2="22"/><line x1="2" y1="12" x2="8" y2="12"/><line x1="16" y1="12" x2="22" y2="12"/></svg></a>'

						L.DomEvent.disableClickPropagation(container)
						L.DomEvent.on(container, 'click', function (e) {
							L.DomEvent.preventDefault(e)
							if (initialBoundsRef.current) {
								map.fitBounds(initialBoundsRef.current, {
									paddingTopLeft: MAP_CONSTANTS.MAP_PADDING.TOP_LEFT,
									paddingBottomRight: MAP_CONSTANTS.MAP_PADDING.BOTTOM_RIGHT,
									maxZoom: MAP_CONSTANTS.ZOOM_LEVELS.MAX_ZOOM,
								})
							} else {
								map.setView(center, zoom)
							}
						})

						return container
					},
				})

				map.addControl(new ResetControl())

				leafletMapRef.current = map

				addBasemap(map, isDarkMode)

				let routeLine
				if (routeData && routeData.polyline) {
					const coordinates = routeData.polyline

					routeLine = L.polyline(coordinates, {
						color: MAP_CONSTANTS.ROUTE_STYLE.COLOR,
						weight: MAP_CONSTANTS.ROUTE_STYLE.WEIGHT,
						opacity: MAP_CONSTANTS.ROUTE_STYLE.OPACITY,
					}).addTo(map)

					// Add weigh stations
					if (routeData.weighStations && routeData.weighStations.length > 0) {
						routeData.weighStations.forEach(station => {
							const weighStationIcon = L.divIcon({
								className: 'll-weigh-station-marker',
								html: `<div class="ll-weigh-station-content">W</div>`,
								iconSize: [24, 24],
								iconAnchor: [12, 12],
							})

							const stationName = escapeHtml(station.name || 'Weigh Station')
							const stationState = escapeHtml(station.state || '')

							L.marker([station.lat, station.lon], { icon: weighStationIcon })
								.addTo(map)
								.bindPopup(
									`<b>Weigh Station</b><br>${stationName}<br>${stationState}`
								)
						})
					}

					// Add toll plazas
					if (
						routeData.tollCosts?.plazas &&
						routeData.tollCosts.plazas.length > 0
					) {
						const tollIcon = L.divIcon({
							className: 'll-map-marker ll-map-marker-toll',
							html: '<div class="ll-map-marker-content"><span>$</span></div>',
							iconSize: [24, 24],
							iconAnchor: [12, 24],
						})

						const tollDetails = routeData.tollCosts.details || []

						routeData.tollCosts.plazas.forEach(plaza => {
							if (plaza.lat && plaza.lon) {
								const plazaName = escapeHtml(plaza.name || 'Toll Plaza')
								const systemName = plaza.system ? escapeHtml(plaza.system) : ''

								const tollDetail = tollDetails.find(
									detail => detail.name === plaza.name
								)

								let popupContent = `<b>Toll Plaza:</b> ${plazaName}`

								if (tollDetail) {
									const paymentMethod =
										tollDetail.paymentMethod === 'videoToll'
											? 'Pay-by-Plate'
											: 'E-ZPass'
									popupContent += `<br><b>Cost:</b> $${Number(
										tollDetail.price
									).toFixed(2)} (${paymentMethod})`
								}

								if (systemName) {
									popupContent += `<br><small>${systemName}</small>`
								}

								L.marker([plaza.lat, plaza.lon], { icon: tollIcon })
									.addTo(map)
									.bindPopup(popupContent)
							}
						})
					}
				}

				// Add stop markers with offset for overlapping stops
				const coordinateGroups = {}
				stops.forEach((stop, index) => {
					const key = `${stop.coordinates[0].toFixed(
						6
					)},${stop.coordinates[1].toFixed(6)}`
					if (!coordinateGroups[key]) {
						coordinateGroups[key] = []
					}
					coordinateGroups[key].push({ stop, index })
				})

				stops.forEach((stop, index) => {
					const isStart = index === 0
					const isEnd = index === stops.length - 1
					const label = index + 1

					const markerIcon = L.divIcon({
						className: `ll-map-marker ${
							isStart
								? 'll-map-marker-start'
								: isEnd
								? 'll-map-marker-end'
								: 'll-map-marker-waypoint'
						}`,
						html: `<div class="ll-map-marker-content"><span>${label}</span></div>`,
						iconSize: [32, 32],
						iconAnchor: [16, 32],
					})

					const stopLabel = isStart
						? 'Start'
						: isEnd
						? 'End'
						: `Stop ${index + 1}`
					const stopName = escapeHtml(stop.name)

					// Check if multiple stops at same location
					const key = `${stop.coordinates[0].toFixed(
						6
					)},${stop.coordinates[1].toFixed(6)}`
					const group = coordinateGroups[key]
					let markerCoords = stop.coordinates

					if (group.length > 1) {
						// Offset overlapping markers in a circular pattern
						const groupIndex = group.findIndex(item => item.index === index)
						const angleStep = (2 * Math.PI) / group.length
						const angle = groupIndex * angleStep
						const offsetDistance = 0.0003 // ~30 meters

						markerCoords = [
							stop.coordinates[0] + offsetDistance * Math.cos(angle),
							stop.coordinates[1] + offsetDistance * Math.sin(angle),
						]
					}

					L.marker(markerCoords, { icon: markerIcon })
						.addTo(map)
						.bindPopup(`<b>${stopLabel}:</b> ${stopName}`)
				})

				setTimeout(() => {
					try {
						// The map may have been replaced before the delay elapsed
						if (leafletMapRef.current !== map) {
							return
						}

						if (routeLine) {
							const bounds = routeLine.getBounds()
							initialBoundsRef.current = bounds
							map.fitBounds(bounds, {
								paddingTopLeft: MAP_CONSTANTS.MAP_PADDING.TOP_LEFT,
								paddingBottomRight: MAP_CONSTANTS.MAP_PADDING.BOTTOM_RIGHT,
								maxZoom: MAP_CONSTANTS.ZOOM_LEVELS.MAX_ZOOM,
							})
						} else {
							initialBoundsRef.current = null
							if (stops.length === 1) {
								map.setView(
									stops[0].coordinates,
									MAP_CONSTANTS.ZOOM_LEVELS.SINGLE_STOP
								)
							}
						}
					} catch (e) {
						console.error('❌ [MAP RENDER] Error fitting bounds:', e)
					}
				}, MAP_CONSTANTS.MAP_UPDATE_DELAY_MS)
			} catch (error) {
				console.error('❌ [MAP RENDER] Error rendering route on map:', error)
				setRouteError('Failed to render route on map')
			}
		}

		return () => {
			removeMap(leafletMapRef.current)
			leafletMapRef.current = null
		}
	}, [routeData, stops, draggedIndex, isDarkMode])

	return (
		<MapErrorBoundary>
			<div className="ll-map-modal-backdrop" onClick={handleBackdropClick}>
				<div
					className={`ll-map-modal ${isDarkMode ? 'll-map-modal-dark' : ''}`}
					ref={modalRef}
					onClick={e => e.stopPropagation()}
				>
					<div className="ll-map-modal-header">
						<div className="ll-map-modal-left">
							<span className="ll-map-modal-logo-text">Maps by</span>
							<a
								href="https://loadline.ai"
								target="_blank"
								rel="noopener noreferrer"
								className="ll-map-modal-brand-link"
							>
								<div className="ll-map-modal-logo">
									<img
										src="/loadline-logo512%20.png"
										alt="LoadLine"
										style={{ width: '100%', height: '100%' }}
									/>
								</div>
								<span className="ll-map-modal-brand-text">LoadLine</span>
							</a>
							{stops.length > 0 && stops[0]?.coordinates?.length >= 2 && (
								<a
									href={(() => {
										if (stops.length === 1) {
											const query = encodeURIComponent(
												`${stops[0].coordinates[0]},${stops[0].coordinates[1]}`
											)
											return `https://www.google.com/maps/search/?api=1&query=${query}`
										}
										const lastStop = stops[stops.length - 1]
										if (!lastStop?.coordinates?.length >= 2) return '#'
										const origin = encodeURIComponent(
											`${stops[0].coordinates[0]},${stops[0].coordinates[1]}`
										)
										const destination = encodeURIComponent(
											`${lastStop.coordinates[0]},${lastStop.coordinates[1]}`
										)
										const waypoints =
											stops.length > 2
												? encodeURIComponent(
														stops
															.slice(1, -1)
															.filter(stop => stop?.coordinates?.length >= 2)
															.map(
																stop =>
																	`${stop.coordinates[0]},${stop.coordinates[1]}`
															)
															.join('|')
												  )
												: ''
										return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${
											waypoints ? `&waypoints=${waypoints}` : ''
										}&travelmode=driving`
									})()}
									target="_blank"
									rel="noopener noreferrer"
									className="ll-map-modal-gmaps-link"
								>
									https://www.google.com/maps
									<svg
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
										<polyline points="15 3 21 3 21 9"></polyline>
										<line x1="10" y1="14" x2="21" y2="3"></line>
									</svg>
								</a>
							)}
						</div>
						<div className="ll-map-modal-actions">
							<div className="ll-map-modal-switch-container">
								<div className="ll-map-modal-switch-label">
									<span className="ll-map-modal-switch-text">
										Open Built-In Map
									</span>
									<label className="ll-map-modal-switch">
										<input
											type="checkbox"
											checked={useEmbeddedMap}
											onChange={handleSwitchToggle}
											className="ll-map-modal-switch-input"
										/>
										<span className="ll-map-modal-switch-slider"></span>
									</label>
								</div>
							</div>
							<button
								className="ll-map-modal-close"
								onClick={e => {
									e.stopPropagation()
									onClose()
								}}
								title="Close"
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
								>
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</button>
						</div>
					</div>

					<div className="ll-map-modal-content">
						<RouteControls
							stops={stops}
							routeData={routeData}
							draggedIndex={draggedIndex}
							onDragStart={handleDragStart}
							onDragOver={handleDragOver}
							onDragEnd={handleDragEnd}
							onDeleteStop={handleDeleteStop}
							onAddStop={handleAddStop}
							onUpdateStop={handleUpdateStop}
							onSearchLocation={handleSearchLocation}
							isDarkMode={isDarkMode}
						/>

						{routeError && (
							<div className="ll-map-modal-error">
								<div className="ll-map-modal-error-icon">⚠️</div>
								<div className="ll-map-modal-error-text">{routeError}</div>
							</div>
						)}

						{(isLoading || isGeocodingStartingLocation) && (
							<div className="ll-map-modal-loading">
								<div className="ll-map-modal-spinner"></div>
								<div>
									{isGeocodingStartingLocation
										? 'Finding your location...'
										: 'Loading route...'}
								</div>
							</div>
						)}

						<div
							ref={mapContainerRef}
							className="ll-leaflet-map-container"
							style={{
								display: !routeError ? 'block' : 'none',
								width: '100%',
								height: '100%',
							}}
						/>
					</div>
				</div>
			</div>
		</MapErrorBoundary>
	)
}

export default MapModal
